import {
  Input as BaseInput,
  NumberInput as BaseNumberInput,
} from '@/components_deprecated/Input'
import { TextArea as BaseTextArea } from '@/components_deprecated/TextArea'
import { GoogleCountryCodes } from '@/utils/consts'
import { getLocale } from '@/utils/date-fns'
import { useGoogleAutocomplete, useMap } from '@/utils/hooks'
import type { UseGoogleAutocompleteOptions } from '@/utils/hooks/useGoogleAutocomplete/types'
import { unlessLoadingDots } from '@/utils/loading'
import type { FormErrors, FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useEffectEvent, useRef, useState } from 'react'
import styled from 'styled-components'
import type { EditIssueForm } from '../types'
import { DetailsField, DetailsSection } from './issue-details-layout'

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`
const Input = styled(BaseInput).attrs({
  grayed: true,
  height: '3rem',
})``
const NumberInput = styled(BaseNumberInput).attrs({
  grayed: true,
  width: '11ch',
  height: '3rem',
})`
  input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`
const TextArea = styled(BaseTextArea).attrs({
  background: 'quaternary',
  height: '5rem',
})``
const PlaceEditor = styled(Editor)`
  flex-direction: row;
`

const CurrencyEditor = styled(Editor)`
  flex-direction: row;

  div.currency {
    display: flex;
    justify-content: center;
    align-items: center;

    height: 3rem;
    padding: 0 0.5rem;

    text-transform: uppercase;
    font-weight: 700;
    font-size: 0.9rem;

    background-color: white;
    border: 1px solid ${props => props.theme.colors.gray9};
    border-left-width: 0;

    border-top-right-radius: ${props => props.theme.sizes.defaultBorderRadius};
    border-bottom-right-radius: ${props =>
      props.theme.sizes.defaultBorderRadius};
  }
`

interface DetailsProps {
  loading: boolean
  editing: boolean
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>

  enableLocationDescription: boolean

  placename: string | undefined
  regNr: string | null | undefined
  damage: string | undefined
  reason: string | undefined
  immediateAction: string | undefined
  action: string | undefined
  locationDescription: string | undefined
  description: string | undefined
  costEstimate: number | undefined
  actualCost: number | null | undefined
  suggestedSolution: string | undefined
  solution: string | undefined

  relatedLink: string | null | undefined

  onLocationUpdate: (
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) => void
  onChange: FormOnChangeFn<EditIssueForm>
}

export const Details: React.FC<DetailsProps> = ({
  loading,
  editing,
  form,
  errors,

  enableLocationDescription,

  placename,
  regNr,
  damage,
  reason,
  immediateAction,
  action,
  description,
  locationDescription,
  costEstimate,
  actualCost,
  suggestedSolution,
  solution,

  relatedLink,

  onLocationUpdate,
  onChange,
}) => {
  const translations = useTranslate({
    unknown: 'common.unknown',

    location: 'common.location',
    equipment: 'issues.equipment-material',
    damage: 'issues.damage-description',
    reason: 'issues.reason',
    immediateAction: 'issues.immediate-action',
    action: 'issues.action',
    description: 'issues.description-of-event-unwanted-event',
    locationDescription: 'issues.location-description',
    suggestedSolution: 'issues.suggested-improvement',
    solution: 'issues.final-solution',
    costEstimate: 'issues.cost-estimation',
    actualCost: 'issues.actual-cost',

    seeMore: 'issues.see-more',

    sectionLocation: 'issues.section-location',
    sectionEvent: 'issues.section-event',
    sectionResolution: 'issues.section-resolution',
    sectionCost: 'issues.section-cost',
  })

  const prevPlacename = useRef<string | null>(null)
  const [descriptionPlaceIdMap, { set: descriptionPlaceIdMapSet }] = useMap<
    string,
    string
  >()
  const [triggerGeocoding, setTriggerGeocoding] = useState(false)

  const handleFindPlacename = useEffectEvent(() => {
    prevPlacename.current = form.placename
    const service = new google.maps.Geocoder()
    const placeIdValue = descriptionPlaceIdMap.get(prevPlacename.current)

    const geocodeOptions =
      // if we have a placeID from the autocomplete this simply needs to be a lookup with the ID, otherwise we try to find something with geocoding
      placeIdValue !== undefined
        ? { placeId: placeIdValue }
        : { address: form.placename }
    void service.geocode(geocodeOptions, onLocationUpdate)

    setTriggerGeocoding(false)
  })

  useEffect(() => {
    prevPlacename.current = form.placename

    if (typeof google === 'undefined' || form.placename.trim() === '') return

    if (triggerGeocoding) handleFindPlacename()
  }, [form.placename, prevPlacename, triggerGeocoding, onLocationUpdate])

  const predictions = useGoogleAutocomplete(form.placename, {
    componentRestrictions: {
      country: [
        // country code restrictions we use for the autocomplete API
        GoogleCountryCodes.Norway,
        GoogleCountryCodes.Sweden,
        GoogleCountryCodes.Denmark,
        GoogleCountryCodes.Finland,
        GoogleCountryCodes.Estonia,
        GoogleCountryCodes.Latvia,
        GoogleCountryCodes.RussianFederation,
        GoogleCountryCodes.Lithuania,
        GoogleCountryCodes.Poland,
        GoogleCountryCodes.Germany,
        GoogleCountryCodes.Belarus,
        GoogleCountryCodes.Ukraine,
        GoogleCountryCodes.CzechRepublic,
      ],
    },
  } as UseGoogleAutocompleteOptions)

  const autoCompleteSource = predictions.predictions?.map(pred => {
    // The description field is the human readable value, whereas if we pick an option from here
    // we want to use the ID related to said description. We do this by making use of a hashmap which encodes a
    // string with an ID which we can use when we want to perform geocoding. If the key already exists we don't bother.
    if (!descriptionPlaceIdMap.has(pred.description))
      descriptionPlaceIdMapSet(pred.description, pred.place_id)

    return pred.description
  })

  function handlePlacenameKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>
  ) {
    const { key } = event
    if (key === 'Enter') {
      setTriggerGeocoding(true)
    }
  }

  function handleAutoCompleteItemSelect(placename: string) {
    onChange('placename')(placename)
    setTriggerGeocoding(true)
  }

  const unlessLoading = unlessLoadingDots(loading, translations.unknown)

  const localeCode = getLocale().code

  return (
    <>
      <DetailsSection title={translations.sectionLocation} icon="location-dot">
        <DetailsField label={translations.location} required={editing} elevated>
          {!editing ? (
            unlessLoading(placename || '-')
          ) : (
            <PlaceEditor>
              <Input
                value={form.placename}
                fullWidth
                autoCompleteSource={autoCompleteSource}
                onAutoCompleteItemSelect={handleAutoCompleteItemSelect}
                onChange={onChange('placename')}
                onKeyDown={handlePlacenameKeyDown}
              />
            </PlaceEditor>
          )}
        </DetailsField>

        <DetailsField label={translations.equipment}>
          {!editing ? (
            unlessLoading(regNr)
          ) : (
            <Editor>
              <Input
                value={form.regNr ?? ''}
                width="16ch"
                onChange={onChange('regNr')}
              />
            </Editor>
          )}
        </DetailsField>

        {enableLocationDescription && (
          <DetailsField label={translations.locationDescription} full>
            {!editing ? (
              unlessLoading(locationDescription || '-')
            ) : (
              <Editor>
                <Input
                  value={form.locationDescription}
                  error={errors.locationDescription}
                  fullWidth
                  onChange={onChange('locationDescription')}
                />
              </Editor>
            )}
          </DetailsField>
        )}
      </DetailsSection>

      <DetailsSection
        title={translations.sectionEvent}
        icon="triangle-exclamation"
      >
        <DetailsField label={translations.description} required={editing} full>
          {!editing ? (
            <>
              <span className="whitespace-pre-wrap">
                {unlessLoading(description || '-')}
              </span>
              {relatedLink && (
                <span className="ml-1">
                  <a href={`${relatedLink}`}>{translations.seeMore}</a>
                </span>
              )}
            </>
          ) : (
            <Editor>
              <TextArea
                value={form.description}
                error={errors.description}
                fullWidth
                onChange={onChange('description')}
              />
            </Editor>
          )}
        </DetailsField>

        <DetailsField label={translations.reason} full>
          {!editing ? (
            unlessLoading(reason || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.reason}
                error={errors.reason}
                fullWidth
                onChange={onChange('reason')}
              />
            </Editor>
          )}
        </DetailsField>

        <DetailsField label={translations.damage} required={editing} full>
          {!editing ? (
            unlessLoading(damage || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.damage}
                error={errors.damage}
                fullWidth
                onChange={onChange('damage')}
              />
            </Editor>
          )}
        </DetailsField>
      </DetailsSection>

      <DetailsSection
        title={translations.sectionResolution}
        icon="screwdriver-wrench"
      >
        <DetailsField label={translations.immediateAction} full>
          {!editing ? (
            unlessLoading(immediateAction || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.immediateAction}
                error={errors.immediateAction}
                fullWidth
                onChange={onChange('immediateAction')}
              />
            </Editor>
          )}
        </DetailsField>

        <DetailsField label={translations.action} full>
          {!editing ? (
            unlessLoading(action || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.action}
                error={errors.action}
                fullWidth
                onChange={onChange('action')}
              />
            </Editor>
          )}
        </DetailsField>

        <DetailsField label={translations.suggestedSolution} full>
          {!editing ? (
            unlessLoading(suggestedSolution || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.suggestedSolution}
                error={errors.suggestedSolution}
                fullWidth
                onChange={onChange('suggestedSolution')}
              />
            </Editor>
          )}
        </DetailsField>

        <DetailsField label={translations.solution} full>
          {!editing ? (
            unlessLoading(solution || '-')
          ) : (
            <Editor>
              <TextArea
                value={form.solution || ''}
                error={errors.solution}
                fullWidth
                onChange={onChange('solution')}
              />
            </Editor>
          )}
        </DetailsField>
      </DetailsSection>

      <DetailsSection title={translations.sectionCost} icon="coins">
        <DetailsField label={translations.costEstimate}>
          {!editing ? (
            unlessLoading(
              (costEstimate ?? 0).toLocaleString(localeCode) + ' NOK'
            )
          ) : (
            <CurrencyEditor>
              <NumberInput
                value={form.estimatedCost}
                onChange={onChange('estimatedCost')}
              />
              <div className="currency">NOK</div>
            </CurrencyEditor>
          )}
        </DetailsField>

        <DetailsField label={translations.actualCost}>
          {!editing ? (
            unlessLoading(
              actualCost ? `${actualCost.toLocaleString(localeCode)} NOK` : '-'
            )
          ) : (
            <CurrencyEditor>
              <NumberInput
                value={form.actualCost ?? 0}
                onChange={onChange('actualCost')}
              />
              <div className="currency">NOK</div>
            </CurrencyEditor>
          )}
        </DetailsField>
      </DetailsSection>
    </>
  )
}
